import { memo, useCallback, useMemo, useState, useRef } from 'react'
import styles from './styles.module.scss'
import cls from 'classnames'
import Ellipsis from '@/components/Ellipsis'
import ScrollableContainer from '@/components/ScrollableContainer'

export type TabItemType<T extends string> = {
  key: T
  label: string | React.ReactNode
  maxWidth?: string | number
  [key: string]: unknown
  children?: React.ReactNode
}

export type TabsProps<T extends string> = {
  items: TabItemType<T>[]
  autoScroll?: boolean
  activeKey?: T
  tabClassName?: string
  size?: 's' | 'm' | 'l'
  onChange?: (key: T) => void
  tabBarExtraContent?: {
    right?: React.ReactNode
    left?: React.ReactNode | string
  }
  scrollable?: boolean
}

const TabLabel = memo(function TabLabel({ label, maxWidth }: { label: React.ReactNode; maxWidth?: string | number }) {
  if (!maxWidth) return <>{label}</>

  return (
    <div style={{ maxWidth: `${maxWidth}px` }}>
      <Ellipsis title={label} lines={1} />
    </div>
  )
})

const TabItem = memo(function TabItem<T extends string>({
  item,
  isActive,
  onClick,
  size,
}: {
  item: TabItemType<T>
  isActive: boolean
  onClick: (key: T, e: React.MouseEvent<HTMLDivElement>) => void
  size: 's' | 'm' | 'l'
}) {
  return (
    <div
      onClick={(e) => onClick(item.key, e)}
      className={cls(styles.tabItem, styles[size], {
        [styles.active]: isActive,
      })}
    >
      <TabLabel label={item.label} maxWidth={item.maxWidth} />
    </div>
  )
})

function Tabs<K extends string>({ size = 'm', scrollable = false, autoScroll = false, ...props }: TabsProps<K>) {
  const [targetScrollLeft, setTargetScrollLeft] = useState<number>()
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const handleTabClick = useCallback(
    (key: K, e: React.MouseEvent<HTMLDivElement>) => {
      props.onChange?.(key)

      if (!autoScroll) return

      const tabElement = e.currentTarget
      const root = scrollContainerRef.current
      const parent = tabElement.parentNode
      if (!root || !parent) return

      const children = Array.from(parent.children)
      const index = children.indexOf(tabElement)
      let scrollPosition = 0
      const containerWidth = root.clientWidth
      const tabOffset = tabElement.offsetLeft
      const tabWidth = tabElement.offsetWidth

      if (index === 0) {
        scrollPosition = 0
      } else if (index === children.length - 1) {
        scrollPosition = (parent as HTMLElement)?.scrollWidth || 0
      } else {
        scrollPosition = tabOffset - (containerWidth - tabWidth) / 2
      }

      setTargetScrollLeft(scrollPosition)
    },
    [props, autoScroll]
  )

  const renderTabItems = useMemo(
    () =>
      props.items.map((item) => (
        <TabItem
          key={item.key}
          item={item}
          isActive={item.key === props.activeKey}
          onClick={(key: string, e: React.MouseEvent<HTMLDivElement>) => handleTabClick(key as K, e)}
          size={size}
        />
      )),
    [props.items, props.activeKey, handleTabClick, size]
  )

  const renderMainContent = useMemo(() => {
    if (scrollable) {
      return (
        <div className={cls(styles.scrollableWrapper)} ref={scrollContainerRef}>
          <ScrollableContainer externalScrollLeft={targetScrollLeft}>{renderTabItems}</ScrollableContainer>
        </div>
      )
    }
    return (
      <div className={cls(styles.tabsGroupScrollBox, { [styles.notScrollable]: !scrollable })} ref={scrollContainerRef}>
        {renderTabItems}
      </div>
    )
  }, [scrollable, targetScrollLeft, renderTabItems])

  const activeContent = useMemo(
    () => props.items.find((item) => item.key === props.activeKey)?.children,
    [props.items, props.activeKey]
  )

  return (
    <>
      <div className={cls(styles.tabsGroup, styles[size], props.tabClassName)}>
        {props.tabBarExtraContent?.left}
        {renderMainContent}
        {<div className={styles.extraRight}>{props.tabBarExtraContent?.right}</div>}
      </div>

      {activeContent && <div className={styles.tabContent}>{activeContent}</div>}
    </>
  )
}

export default memo(Tabs)
